/**
 * Requisition Fulfillment Queue
 * Permissions (confirmed 2026-07-09): HR_REQUISITION_FULFILL_STOCK (Store Officer) — only ever
 * granted to PROCUREMENT_OFFICER via migration V69 until STORE_OFFICER was added in V106 — plus
 * whatever gates the Asset Manager side (unconfirmed; ASSET_ASSIGN previously assumed here).
 *
 * Shows approved requisitions waiting for Store Officer or Asset Manager action.
 * - GOODS type → stock check + issue from inventory
 * - ASSET_REQUEST type → pick actual available assets per line, filtered by category
 */
import { useState } from "react";
import {
  Package, PackageCheck, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle, ArrowUpRight,
} from "lucide-react";
import { hrRequisitionApi } from "../api";
import { assetApi } from "#/features/assets/api";
import { useAsyncData } from "#/features/inventory/hooks";
import type {
  AssetAvailabilityLine, FulfillmentQueueItem, HrRequisition, HrRequisitionLine, StockCheckResponse,
  IssueStockRequest, FulfillAssetRequest, RequisitionType,
} from "../types";

// ─── helpers ─────────────────────────────────────────────────────────────────

function typeBadge(type?: RequisitionType | string | null) {
  const map: Record<string, string> = {
    GOODS:         "bg-emerald-100 text-emerald-700",
    ASSET_REQUEST: "bg-indigo-100 text-indigo-700",
    SERVICE:       "bg-blue-100 text-blue-700",
    WORKS:         "bg-amber-100 text-amber-700",
    GENERAL:       "bg-slate-100 text-slate-600",
  };
  const labels: Record<string, string> = {
    GOODS: "Goods", ASSET_REQUEST: "Asset Request",
    SERVICE: "Service", WORKS: "Works", GENERAL: "General",
  };
  const key = type ?? "";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${map[key] ?? "bg-slate-100 text-slate-500"}`}>
      {labels[key] ?? key}
    </span>
  );
}

function fmtDate(s?: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Stock Issue Panel ────────────────────────────────────────────────────────
// Exported so RequisitionViewPage can offer fulfillment directly on the detail page — a
// requisition that's had one line fulfilled but still has others pending can fall out of the
// pending-fulfillment queue (server-side query scopes to APPROVED only) with no other way to
// finish it in-app otherwise. See RequisitionViewPage's fulfillment section.

export function StockIssuePanel({ req, onDone }: { req: HrRequisition; onDone: () => void }) {
  const [stockCheck, setStockCheck] = useState<StockCheckResponse | null>(null);
  const [checking, setChecking]     = useState(false);
  const [qtys, setQtys]             = useState<Record<number, number>>({});
  const [issuing, setIssuing]       = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [notes, setNotes]           = useState("");
  const [error, setError]           = useState<string | null>(null);

  async function doCheck() {
    setChecking(true); setError(null);
    try {
      const sc = await hrRequisitionApi.stockCheck(req.id);
      setStockCheck(sc);
      const init: Record<number, number> = {};
      (sc.lines ?? []).forEach(l => { init[l.lineId] = Math.min(l.requestedQuantity, l.availableStock); });
      setQtys(init);
    } catch (e: unknown) { setError(String((e as { message?: string })?.message ?? e)); }
    setChecking(false);
  }

  async function doIssue() {
    if (!stockCheck) return;
    setIssuing(true); setError(null);
    const payload: IssueStockRequest = {
      issuances: (stockCheck.lines ?? []).map(l => ({ lineId: l.lineId, quantityIssued: qtys[l.lineId] ?? 0 })),
      notes: notes || undefined,
    };
    try {
      await hrRequisitionApi.issueStock(req.id, payload);
      onDone();
    } catch (e: unknown) { setError(String((e as { message?: string })?.message ?? e)); }
    setIssuing(false);
  }

  async function doEscalate() {
    setEscalating(true); setError(null);
    try {
      await hrRequisitionApi.escalateToProcurement(req.id, { purpose: req.purpose ?? undefined });
      onDone();
    } catch (e: unknown) { setError(String((e as { message?: string })?.message ?? e)); }
    setEscalating(false);
  }

  const scLines = stockCheck?.lines ?? [];
  const canIssue = stockCheck && Object.values(qtys).some(q => q > 0);
  const hasShortfall = stockCheck && scLines.some(l => !l.canFulfill);
  // Some requisitions come back from stock-check with zero lines even though the requisition
  // itself has lines (e.g. a GOODS line wasn't linked to a catalog inventoryItemId at creation
  // time). Surface that plainly instead of a silent empty table, since "no data" here looks
  // identical to "nothing requested" otherwise.
  const noMatchedLines = stockCheck && scLines.length === 0;

  // Once escalated, the backend rejects a second escalate-to-procurement call outright (400 —
  // "already escalated to PR ..."). Stop offering the action instead of letting the user hit
  // that error on a second attempt (e.g. after reloading the page and re-running Check Stock).
  const escalatedTo = req.escalatedToPrNo ?? req.escalatedToPrSerialNo;
  if (escalatedTo) {
    return (
      <div className="mt-3 rounded-2xl border border-blue-200 bg-blue-50/50 p-4 text-sm text-blue-700">
        This requisition was already escalated to procurement as <strong>{escalatedTo}</strong>.
        No further stock action is needed here — track progress on the linked Purchase Requisition instead.
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-emerald-800 text-sm">Issue from Stock</p>
        {!stockCheck && (
          <button onClick={doCheck} disabled={checking}
            className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {checking ? "Checking…" : "Check Stock"}
          </button>
        )}
      </div>

      {noMatchedLines && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Stock check returned no matching items for this requisition. This usually means the
          requested line(s) weren't linked to an inventory catalog item when the requisition was
          created, so there's nothing here to check availability against.
          {req.lines?.length ? (
            <ul className="mt-2 list-disc pl-5 text-xs text-amber-700">
              {req.lines.map(l => (
                <li key={l.id ?? l.lineOrder}>
                  {l.description}{l.quantity ? ` × ${l.quantity}` : ""}
                  {!l.inventoryItemId && <span className="ml-1 italic">— not linked to an inventory item</span>}
                </li>
              ))}
            </ul>
          ) : null}
          {error && <p className="mt-2 text-sm font-medium text-red-700">{error}</p>}
          <button onClick={doEscalate} disabled={escalating}
            className="mt-3 rounded-xl border border-amber-400 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 flex items-center gap-1.5 disabled:opacity-60">
            <ArrowUpRight className="h-3.5 w-3.5" />
            {escalating ? "Escalating…" : "Escalate to Procurement Instead"}
          </button>
        </div>
      )}

      {stockCheck && !noMatchedLines && (
        <>
          <div className="rounded-xl border bg-white overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-right">Requested</th>
                  <th className="px-3 py-2 text-right">Available</th>
                  <th className="px-3 py-2 text-right w-28">Qty to Issue</th>
                  <th className="px-3 py-2 text-center w-20">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {scLines.map(l => (
                  <tr key={l.lineId} className={!l.canFulfill ? "bg-red-50/50" : ""}>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {l.itemName ?? l.description}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{l.requestedQuantity}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${l.canFulfill ? "text-emerald-700" : "text-red-600"}`}>
                      {l.availableStock}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" min={0} max={l.availableStock}
                        value={qtys[l.lineId] ?? 0}
                        onChange={(e) => setQtys(q => ({ ...q, [l.lineId]: Number(e.target.value) }))}
                        className="w-full rounded-lg border px-2 py-1 text-right text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {l.canFulfill
                        ? <CheckCircle className="inline h-4 w-4 text-emerald-500" />
                        : <AlertTriangle className="inline h-4 w-4 text-red-500" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Any notes for this issuance…" />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex flex-wrap gap-2">
            <button onClick={doIssue} disabled={!canIssue || issuing}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              {issuing ? "Issuing…" : "Issue Stock"}
            </button>
            {hasShortfall && (
              <button onClick={doEscalate} disabled={escalating}
                className="rounded-xl border border-amber-400 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 flex items-center gap-1.5 disabled:opacity-60">
                <ArrowUpRight className="h-4 w-4" />
                {escalating ? "Escalating…" : "Escalate Shortfall to Procurement"}
              </button>
            )}
          </div>
        </>
      )}

      {error && !stockCheck && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

// ─── Per-line asset picker ────────────────────────────────────────────────────

function AssetLinePicker({
  line, hint, selected, onChange,
}: {
  line: HrRequisitionLine;
  hint?: AssetAvailabilityLine;
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  // asset-availability only gives a count — fetch the real, pickable Asset records
  // ourselves, filtered by this line's category and AVAILABLE status.
  const { data: assets, loading } = useAsyncData(
    () => assetApi.listAssets(0, 100, { categoryId: line.assetCategoryId ?? undefined, status: "AVAILABLE" }).then(r => r.content),
    [line.assetCategoryId],
  );

  function toggle(id: number) {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  }

  return (
    <div className="rounded-xl border bg-white p-3">
      <p className="text-sm font-semibold text-slate-800">{line.description}</p>
      {line.assetCategoryName && <p className="text-xs text-slate-400">Category: {line.assetCategoryName}</p>}
      {hint && (
        <p className={`mt-0.5 text-xs font-medium ${hint.availability === "SUFFICIENT" ? "text-emerald-600" : "text-red-600"}`}>
          {hint.availableCount} available · need {hint.requestedQuantity}
        </p>
      )}
      <div className="mt-2">
        <label className="block text-xs font-semibold text-slate-500 mb-1">
          Select Assets ({selected.length}/{line.quantity})
        </label>
        {loading && <p className="text-xs text-slate-400">Loading available assets…</p>}
        {!loading && (assets ?? []).length === 0 && (
          <p className="text-xs text-slate-400">No available assets in this category.</p>
        )}
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {(assets ?? []).map(a => (
            <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-1.5 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} />
              {a.name} ({a.assetNumber}) · {a.condition}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Asset Fulfill Panel ──────────────────────────────────────────────────────

// Exported for the same reason as StockIssuePanel above — see that comment.
export function AssetFulfillPanel({ req, onDone }: { req: HrRequisition; onDone: () => void }) {
  const [picks, setPicks]       = useState<Record<number, number[]>>({});
  const [fulfilling, setFulF]   = useState(false);
  const [notes, setNotes]       = useState("");
  const [error, setError]       = useState<string | null>(null);

  // Count-only hint (requested vs available) per line — real pickable assets come from
  // AssetLinePicker's own per-line fetch, not from this endpoint.
  const { data: availability } = useAsyncData(
    () => hrRequisitionApi.assetAvailability(req.id),
    [req.id],
  );

  function hintFor(lineId: number) {
    return availability?.lines?.find(l => l.lineId === lineId);
  }

  async function doFulfill() {
    setFulF(true); setError(null);
    const assignments = Object.entries(picks)
      .filter(([, assetIds]) => assetIds.length > 0)
      .map(([lineId, assetIds]) => ({ lineId: Number(lineId), assetIds }));
    if (!assignments.length) { setError("Please select at least one asset."); setFulF(false); return; }
    const payload: FulfillAssetRequest = { assignments, notes: notes || undefined };
    try {
      await hrRequisitionApi.fulfillAsset(req.id, payload);
      onDone();
    } catch (e: unknown) { setError(String((e as { message?: string })?.message ?? e)); }
    setFulF(false);
  }

  // Only show lines that still need assets — a line already marked FULFILLED (from an earlier,
  // partial pass through this panel) shouldn't be re-offered for assignment.
  const pendingLines = (req.lines ?? []).filter(line => line.fulfillmentStatus !== "FULFILLED");

  return (
    <div className="mt-3 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4 space-y-4">
      <p className="font-semibold text-indigo-800 text-sm">Assign Assets</p>

      {pendingLines.length === 0 ? (
        <p className="text-sm text-indigo-700">All lines on this requisition already have assets assigned.</p>
      ) : (
        <>
          <div className="space-y-3">
            {pendingLines.map(line => (
              <AssetLinePicker
                key={line.id}
                line={line}
                hint={line.id != null ? hintFor(line.id) : undefined}
                selected={picks[line.id!] ?? []}
                onChange={(ids) => setPicks(p => ({ ...p, [line.id!]: ids }))}
              />
            ))}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Notes (optional)</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Any notes for this fulfillment…" />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button onClick={doFulfill} disabled={fulfilling}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            {fulfilling ? "Assigning…" : "Assign Remaining Assets"}
          </button>
        </>
      )}
    </div>
  );
}

// ─── Queue Row ────────────────────────────────────────────────────────────────

function QueueRow({ item, onRefresh }: { item: FulfillmentQueueItem; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail]     = useState<HrRequisition | null>(null);
  const [loading, setLoading]   = useState(false);

  async function toggle() {
    if (!expanded && !detail) {
      setLoading(true);
      try { setDetail(await hrRequisitionApi.get(item.id)); }
      catch { /* ignore */ }
      setLoading(false);
    }
    setExpanded(e => !e);
  }

  const itemType = item.requisitionType ?? item.type;
  const isGoods = itemType === "GOODS";
  const isAsset = itemType === "ASSET_REQUEST";

  return (
    <div className="border-b last:border-0">
      <div className="flex items-center gap-4 px-5 py-4">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isGoods ? "bg-emerald-50" : "bg-indigo-50"}`}>
          {isGoods
            ? <Package className="h-5 w-5 text-emerald-600" />
            : <PackageCheck className="h-5 w-5 text-indigo-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800">{item.serialNo ?? `#${item.id}`}</span>
            {typeBadge(itemType)}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {item.requesterName ?? "—"}{item.departmentName ? ` · ${item.departmentName}` : ""}{" "}
            · {item.lineCount ?? "?"} line{(item.lineCount ?? 0) !== 1 ? "s" : ""}
            {item.submittedAt ? ` · Submitted ${fmtDate(item.submittedAt)}` : ""}
          </div>
          {item.purpose && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.purpose}</p>}
        </div>
        <button onClick={toggle} disabled={loading}
          className="flex items-center gap-1 rounded-xl border px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {loading ? "Loading…" : expanded ? <><ChevronUp className="h-4 w-4" />Collapse</> : <><ChevronDown className="h-4 w-4" />Fulfill</>}
        </button>
      </div>

      {expanded && detail && (
        <div className="px-5 pb-5">
          {isGoods && <StockIssuePanel req={detail} onDone={() => { setExpanded(false); onRefresh(); }} />}
          {isAsset && <AssetFulfillPanel req={detail} onDone={() => { setExpanded(false); onRefresh(); }} />}
          {!isGoods && !isAsset && (
            <div className="rounded-2xl border bg-slate-50 p-4 text-sm text-slate-500">
              {itemType} requisitions do not require stock or asset fulfillment actions.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RequisitionFulfillmentPage() {
  const { data, loading, error, reload } = useAsyncData(
    () => hrRequisitionApi.pendingFulfillment({ size: 50 }),
    [],
  );

  const items = data?.content ?? [];
  const goodsCount = items.filter(i => (i.requisitionType ?? i.type) === "GOODS").length;
  const assetCount = items.filter(i => (i.requisitionType ?? i.type) === "ASSET_REQUEST").length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Requisitions</p>
        <h1 className="text-xl font-bold text-slate-900">Fulfillment Queue</h1>
      </div>

      {/* KPI chips */}
      {!loading && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: "Total Pending", value: items.length, cls: "bg-slate-50 text-slate-700" },
            { label: "Goods (Stock)", value: goodsCount, cls: "bg-emerald-50 text-emerald-700" },
            { label: "Asset Requests", value: assetCount, cls: "bg-indigo-50 text-indigo-700" },
          ].map(c => (
            <div key={c.label} className={`rounded-2xl border p-4 ${c.cls}`}>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-70">{c.label}</p>
              <p className="text-2xl font-bold mt-1">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Queue */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
        {loading && (
          <div className="px-5 py-10 text-center text-sm text-slate-400">Loading fulfillment queue…</div>
        )}
        {error && (
          <div className="px-5 py-6 text-center text-sm text-red-600">{error}</div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="px-5 py-10 text-center space-y-2">
            <CheckCircle className="mx-auto h-8 w-8 text-emerald-300" />
            <p className="text-sm font-semibold text-slate-500">All caught up!</p>
            <p className="text-xs text-slate-400">No approved requisitions are waiting for fulfillment.</p>
          </div>
        )}
        {!loading && items.map(item => (
          <QueueRow key={item.id} item={item} onRefresh={reload} />
        ))}
      </div>
    </div>
  );
}
